import { IEvent, IRunningOrderDogClass, IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

/**
 * Absolute start/end for every ring, judge, and class on a show.
 *
 * Start times are derived, never stored. This matches the Judge Schedules tab
 * clock (`getShowStartTime` + `getJudgeTiming` + `getElementTiming`):
 * a trailing show on a ring starts when that same ring name finishes on the
 * previous show, judges run back to back, and a class starts when the previous
 * class on that judge ends. Grouped children (`group_with`) do not add time;
 * the parent's stored minutes already include them.
 *
 * `scores` and `event` are accepted so callers can pass the same arguments as
 * `calculateClassStartTime`. The clock uses the minutes already on each tile
 * (the tab enriches those from the running-order summary before display).
 */

export interface TimelineClassSlot {
  classIndex: number;
  isBreak: boolean;
  minutes: number;
  start: Date;
  end: Date;
}

export interface TimelineJudgeSlot {
  judgeIndex: number;
  judgeName: string;
  judgeNumber?: string;
  start: Date;
  end: Date;
  totalMinutes: number;
  judgingMinutes: number;
  classes: TimelineClassSlot[];
}

export interface TimelineRingSlot {
  ring: string;
  start: Date;
  end: Date;
  totalMinutes: number;
  judges: TimelineJudgeSlot[];
}

export interface JudgeScheduleTimeline {
  showStart: Date;
  showEnd: Date;
  rings: TimelineRingSlot[];
}

export function classBlockMinutes(dogClass: IRunningOrderDogClass): number {
  if (dogClass.group_with) {
    return 0;
  }
  if (dogClass.type === Constants.running_order_dog_class_type.break) {
    return dogClass.duration || dogClass.total_minutes || 0;
  }
  if (dogClass.total_minutes) {
    return dogClass.total_minutes;
  }
  if (dogClass.duration && dogClass.entries) {
    return dogClass.duration * dogClass.entries;
  }
  return 0;
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60 * 1000);
}

function sameCalendarDay(a: string | Date, b: string | Date): boolean {
  const left = new Date(a);
  const right = new Date(b);
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

/**
 * The show this trailing show follows.
 * `previous_show_id` is the real link. Name matching is only a fallback, and
 * the same trial name repeats on later days, so a name hit has to be the
 * copy on this show's date. Otherwise the bars land on an earlier day and
 * disappear from the day being viewed.
 */
function findPreviousShow(show: IShow, allShows: IShow[]): IShow | undefined {
  if (!show.trailing_show) {
    return undefined;
  }
  const selfId = show._id?.toString();
  if (show.previous_show_id) {
    const byId = allShows.find((candidate) => candidate._id?.toString() === String(show.previous_show_id));
    if (byId && byId._id?.toString() !== selfId) {
      return byId;
    }
  }
  if (!show.previous_show_name) {
    return undefined;
  }
  const named = allShows.filter((candidate) => {
    if (selfId && candidate._id?.toString() === selfId) {
      return false;
    }
    return candidate.show_name === show.previous_show_name
      || `${candidate.show_type} ${candidate.show_name}` === show.previous_show_name;
  });
  if (named.length <= 1) {
    return named[0];
  }
  const sameDay = named.find((candidate) => sameCalendarDay(candidate.show_date, show.show_date));
  if (sameDay) {
    return sameDay;
  }
  const trailingMs = new Date(show.show_date).getTime();
  const earlier = named
    .filter((candidate) => new Date(candidate.show_date).getTime() <= trailingMs)
    .sort((a, b) => new Date(b.show_date).getTime() - new Date(a.show_date).getTime());
  return earlier[0] || named[0];
}

function ringMinutes(show: IShow, ringName: string): number {
  const ring = (show.rings || []).find((candidate) => candidate.ring === ringName);
  if (!ring?.running_order) {
    return 0;
  }
  return ring.running_order.reduce((sum, judge) => {
    return sum + (judge.dog_classes || []).reduce((classSum, dogClass) => classSum + classBlockMinutes(dogClass), 0);
  }, 0);
}

/** Minutes a ring was dragged later than the show start. Missing or negative values count as 0. */
export function ringStartOffsetMinutes(show: { rings?: { ring?: string; start_offset_minutes?: number }[] } | undefined, ringName?: string): number {
  if (!show || !ringName) {
    return 0;
  }
  const minutes = show.rings?.find((ring) => ring.ring === ringName)?.start_offset_minutes || 0;
  return minutes > 0 ? minutes : 0;
}

/** Ring start, chaining a trailing show onto the same ring name of the previous show. */
export function ringStartTime(show: IShow, ringName: string, allShows: IShow[] = []): Date {
  const previous = findPreviousShow(show, allShows);
  const ownOffset = ringStartOffsetMinutes(show, ringName);
  if (!previous) {
    return addMinutes(new Date(show.show_date), ownOffset);
  }
  return addMinutes(
    new Date(previous.show_date),
    ringStartOffsetMinutes(previous, ringName) + ringMinutes(previous, ringName) + ownOffset
  );
}

export function buildJudgeScheduleTimeline(
  show: IShow,
  allShows: IShow[] = [],
  _scores?: unknown[],
  _event?: IEvent
): JudgeScheduleTimeline {
  const rings: TimelineRingSlot[] = (show.rings || []).map((ring) => {
    const start = ringStartTime(show, ring.ring, allShows);
    let cursor = start;
    const judges: TimelineJudgeSlot[] = (ring.running_order || []).map((judge, judgeIndex) => {
      const judgeStart = cursor;
      const classes: TimelineClassSlot[] = [];
      let totalMinutes = 0;
      let judgingMinutes = 0;

      (judge.dog_classes || []).forEach((dogClass, classIndex) => {
        const minutes = classBlockMinutes(dogClass);
        const classStart = cursor;
        const classEnd = addMinutes(classStart, minutes);
        classes.push({
          classIndex,
          isBreak: dogClass.type === Constants.running_order_dog_class_type.break,
          minutes,
          start: classStart,
          end: classEnd,
        });
        cursor = classEnd;
        totalMinutes += minutes;
        if (dogClass.type !== Constants.running_order_dog_class_type.break && !dogClass.group_with) {
          judgingMinutes += minutes;
        }
      });

      return {
        judgeIndex,
        judgeName: judge.judge_name,
        judgeNumber: judge.judge_number,
        start: judgeStart,
        end: cursor,
        totalMinutes,
        judgingMinutes,
        classes,
      };
    });

    const totalMinutes = judges.reduce((sum, judge) => sum + judge.totalMinutes, 0);
    return {
      ring: ring.ring,
      start,
      end: addMinutes(start, totalMinutes),
      totalMinutes,
      judges,
    };
  });

  const showStart = rings.reduce<Date | undefined>((earliest, ring) => {
    if (!earliest || ring.start.getTime() < earliest.getTime()) {
      return ring.start;
    }
    return earliest;
  }, undefined) || new Date(show.show_date);

  const showEnd = rings.reduce<Date>((latest, ring) => {
    return ring.end.getTime() > latest.getTime() ? ring.end : latest;
  }, showStart);

  return { showStart, showEnd, rings };
}
